import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { addTimeOffRequest } from "../api/timeOffRequest.js";
import { selectUserSession } from "../slices/userSession/userSessionSlice.js";
import {
  ACTION_APPROVE,
  ACTION_CANCEL,
  ACTION_CANCEL_APPROVE,
  ACTION_CANCEL_REFUSE,
  ACTION_REFUSE,
  ACTION_REINSTATE,
  STATUS_APPROVED,
  STATUS_CANCELED_ADM,
  STATUS_CANCELED_EE,
  STATUS_CANCEL_REFUSED,
  STATUS_REFUSED,
} from "../util/constants.js";
import { UNKNOWN_ACTION_MESSAGE } from "../util/messageConstants.js";
import { buildTimeOffRequestEventPayload } from "../util/timeOffRequestEventBuilder.js";
import { validateTimeOffRequestEventSequence } from "../util/timeOffRequestEventValidation.js";
import ConfirmButton from "./fundamentals/ConfirmButton.jsx";
import { isLWOPTOWOPRequest } from "../util/timeOffRequestUtils.js";

const TimeOffRequestAdminActionConfirm = (props) => {
  const userSession = useSelector(selectUserSession);

  const getActionConfirmButtonBasedOnActionSelected = (action) => {
    switch (action) {
      case ACTION_APPROVE:
        return getActionConfirmButton(STATUS_APPROVED);
      case ACTION_REFUSE:
        return getActionConfirmButton(STATUS_REFUSED);
      case ACTION_CANCEL_APPROVE:
        return getActionConfirmButton(STATUS_CANCELED_EE);
      case ACTION_CANCEL_REFUSE:
        return getActionConfirmButton(STATUS_CANCEL_REFUSED);
      case ACTION_CANCEL:
        return getActionConfirmButton(STATUS_CANCELED_ADM);
      case ACTION_REINSTATE:
        return getActionConfirmButton(STATUS_APPROVED, true);
      default:
        return UNKNOWN_ACTION_MESSAGE(props.actionSelected);
    }
  };

  const getActionConfirmButton = (statusName, isReinstated = false) => {
    return (
      <ConfirmButton
        variant="contained"
        color="primary"
        onClick={() => handleActionClicked(statusName, isReinstated)}
        isLoading={props.isActionLoading}
      >
        Yes
      </ConfirmButton>
    );
  };

  const prepareTimeOffRequestData = () => {
    let timeOffRequestData = cleanupReasonData({ ...props.timeOffRequest });

    let requestingShiftsObj = {
      timeOffRequestType: timeOffRequestData.timeOffRequestType,
      shiftStart: timeOffRequestData.shiftStart,
      shiftEnd: timeOffRequestData.shiftEnd,
    };
    if (isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType)) {
      if (props.actionSelected === ACTION_APPROVE) {
        const approvedTimings = props.getApprovedTimings();
        timeOffRequestData.approvedWOPStart = approvedTimings.approvedWOPStart;
        timeOffRequestData.approvedWOPEnd = approvedTimings.approvedWOPEnd;
      }

      requestingShiftsObj = {
        ...requestingShiftsObj,
        preferredWOPStart: timeOffRequestData.preferredWOPStart,
        preferredWOPEnd: timeOffRequestData.preferredWOPEnd,
        wopASAP: timeOffRequestData.wopASAP,
        phoneNumber: timeOffRequestData.phoneNumber,
        ...(timeOffRequestData.approvedWOPStart ? { approvedWOPStart: timeOffRequestData.approvedWOPStart } : {}),
        ...(timeOffRequestData.approvedWOPEnd ? { approvedWOPEnd: timeOffRequestData.approvedWOPEnd } : {}),
      };
    }
    timeOffRequestData.requestingShifts = [requestingShiftsObj];
    return timeOffRequestData;
  };

  const handleActionClicked = async (statusName, isReinstated) => {
    if (!props.isFormValidationSuccess()) {
      return;
    }

    props.onSetIsActionLoading(true);

    let timeOffRequestData = prepareTimeOffRequestData();

    // Specific Action Types require some different data to be sent...
    if (statusName === STATUS_APPROVED && isReinstated) {
      timeOffRequestData.isReinstated = true;
    }

    if (containsCannedReason()) {
      timeOffRequestData.cannedReason = props.cannedReason;
      timeOffRequestData.reasonComments = props.reasonComments;
    } else if (containsReasonCommentsOnly()) {
      timeOffRequestData.reasonComments = props.reasonComments;
    }

    let payload = buildTimeOffRequestEventPayload(statusName, timeOffRequestData, userSession);

    let timeOffRequestEventSequenceValidationErrors = await validateTimeOffRequestEventSequence(
      timeOffRequestData.torId,
      statusName
    );

    if (timeOffRequestEventSequenceValidationErrors !== "") {
      props.onSetActionErrors(timeOffRequestEventSequenceValidationErrors);
      props.onSetIsActionLoading(false);
      props.onSetIsActionError(true);
    } else {
      addTimeOffRequest(payload)
        .then((res) => res.json())
        .then((result) => {
          if (result.error) {
            console.error("Error");
            props.onSetActionErrors(result.error);
            props.onSetIsActionLoading(false);
            props.onSetIsActionError(true);
          } else if (result.newTimeOffRequestEvents.length > 0) {
            props.onClickDialogClose();
            if (props.onSetIsActionSuccess) {
              props.onSetIsActionSuccess(true);
            }
          } else {
            console.error("Unknown response");
          }
        });
    }
  };

  const cleanupReasonData = (timeOffRequest) => {
    if (timeOffRequest.cannedReason) {
      delete timeOffRequest["cannedReason"];
    }
    if (timeOffRequest.reasonComments) {
      delete timeOffRequest["reasonComments"];
    }
    return timeOffRequest;
  };

  const containsCannedReason = () => {
    return props.cannedReason !== "";
  };

  const containsReasonCommentsOnly = () => {
    return props.cannedReason === "" && props.reasonComments !== "";
  };

  return getActionConfirmButtonBasedOnActionSelected(props.actionSelected);
};

TimeOffRequestAdminActionConfirm.propTypes = {
  timeOffRequest: PropTypes.object.isRequired,
  actionSelected: PropTypes.string.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  onSetIsActionError: PropTypes.func.isRequired,
  onSetActionErrors: PropTypes.func.isRequired,
  onSetIsActionLoading: PropTypes.func.isRequired,
  onSetIsActionSuccess: PropTypes.func.isRequired,
  isActionLoading: PropTypes.bool.isRequired,
  cannedReason: PropTypes.string.isRequired,
  reasonComments: PropTypes.string.isRequired,
  isFormValidationSuccess: PropTypes.func.isRequired,
  getApprovedTimings: PropTypes.func,
};

export default TimeOffRequestAdminActionConfirm;
